import Constants from 'expo-constants';
import { useCallback, useState } from 'react';


import {
  addPaymentAuthorizationClosedListener,
  addPaymentAuthorizationListener,
  canMakePayments,
  makePaymentRequest,
} from '@fhs/expo-applepay';
import { PaymentProcessor } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import logger from '@fhs-legacy/frontend/src/utils/logger';

import type { ApplePayDetails, IAppleConfig } from '../../../cart/src/payment/payment-types';

interface ApplePayForReloadConfig {
  processor: PaymentProcessor;
}

interface PaymentConfig {
  region: 'US' | 'CA';
  currencyCode: string;
  totalAmount: string;
  merchantId: string;
  payeeName: string;
}

interface IApplePayResult {
  paymentData: string;
  billingInformation: string;
  paymentType: string;
  displayName: string;
  customerFamilyName?: string;
  customerGivenName?: string;
  customerNickname?: string;
}

interface IApplePayParsedData {
  signature: string;
  version: string;
  data: string;
  header: {
    ephemeralPublicKey: string;
    publicKeyHash: string;
    transactionId: string;
  };
}

interface BillingAddress {
  locality: string;
  postalCode: string;
  region: string;
  streetAddress: string;
}

/**
 * Hook to trigger Apple Pay payment sheet for gift card reload
 *
 * This is a standalone hook that can be used outside of the payment pipeline
 * to trigger Apple Pay for reload operations.
 */
export function useApplePayForReload(config: ApplePayForReloadConfig) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const region: 'US' | 'CA' = Constants?.expoConfig?.extra?.eas?.buildRegion ?? 'US';

  const getConfiguration = useCallback((): IAppleConfig | null => {
    const appleConfig: IAppleConfig = Constants?.expoConfig?.extra?.eas?.apple;

    if (!appleConfig) {
      logger.error({
        message: '[ApplePay Reload] Apple Pay configuration not found',
      });
      return null;
    }

    return appleConfig;
  }, []);

  const buildPaymentConfig = useCallback(
    (amount: number, appleConfig: IAppleConfig): PaymentConfig => {
      const currencyCode = region === 'CA' ? 'CAD' : 'USD';
      const totalAmount = (amount / 100).toString();

      // For prepaid/reload, use prepaid merchant ID if available
      const merchantId = appleConfig.prepaidMerchantId || appleConfig.merchantId;

      return {
        region,
        currencyCode,
        totalAmount,
        merchantId,
        payeeName: 'Firehouse Subs',
      };
    },
    [region]
  );

  const parseBillingAddress = useCallback((billingInfo: any): BillingAddress => {
    const streetAddress = [billingInfo.street, billingInfo.street2].filter(Boolean).join(', ');

    return {
      locality: billingInfo.city || '',
      postalCode: billingInfo.postalCode || '',
      region: billingInfo.state || '',
      streetAddress,
    };
  }, []);

  const formatBillingAddress = useCallback((address: BillingAddress): string => {
    return [address.streetAddress, address.locality, address.region, address.postalCode]
      .filter(Boolean)
      .join(', ');
  }, []);

  const parsePaymentResponse = useCallback(
    (result: IApplePayResult): ApplePayDetails => {
      const { paymentData, billingInformation, paymentType, displayName } = result;

      let parsedPaymentData: IApplePayParsedData;
      let parsedBillingInformation: any;

      try {
        parsedPaymentData = JSON.parse(paymentData);
      } catch (err) {
        logger.error({
          message: '[ApplePay Reload] Failed to parse payment data',
          error: err,
        });
        throw new Error('Invalid payment data received from Apple Pay');
      }

      try {
        parsedBillingInformation = JSON.parse(billingInformation);
      } catch (err) {
        logger.error({
          message: '[ApplePay Reload] Failed to parse billing information',
          error: err,
        });
        throw new Error('Invalid billing information received from Apple Pay');
      }

      const {
        signature,
        version,
        data,
        header: { ephemeralPublicKey, publicKeyHash, transactionId },
      } = parsedPaymentData;

      const billingAddress = parseBillingAddress(parsedBillingInformation);
      const formattedAddress = formatBillingAddress(billingAddress);

      return {
        signature,
        data,
        version,
        ephemeralPublicKey,
        publicKeyHash,
        transactionId,
        decryptAlias: config.processor.toLowerCase(),
        applicationData: '',
        country: region,
        formatted: formattedAddress,
        paymentData,
        primary: true,
        type: 'work',
        billingAddress,
        PaymentMethodData: {
          paymentType: paymentType.toLowerCase(),
          displayName,
        },
      };
    },
    [config.processor, region, parseBillingAddress, formatBillingAddress]
  );

  const validateApplePayAvailability = useCallback(
    async (appleConfig: IAppleConfig): Promise<boolean> => {
      try {
        logger.info({
          message: '[ApplePay Reload] Validating Apple Pay availability',
          hasNetworks: !!appleConfig.paymentsNetworks,
          networksCount: appleConfig.paymentsNetworks?.length || 0,
        });

        const { available, hasCards } = await canMakePayments({
          networks: appleConfig.paymentsNetworks,
        });

        if (!available) {
          logger.warn({
            message: '[ApplePay Reload] Apple Pay not available on this device',
          });
          setError('Apple Pay is not available on this device');
          return false;
        }

        if (!hasCards) {
          logger.warn({
            message: '[ApplePay Reload] No cards configured in Apple Wallet',
          });
          setError('No payment cards found in Apple Wallet');
          return false;
        }

        logger.info({
          message: '[ApplePay Reload] Apple Pay is available and has cards',
        });

        return true;
      } catch (err) {
        logger.error({
          message: '[ApplePay Reload] Failed to check Apple Pay availability',
          error: err,
        });
        setError('Could not verify Apple Pay availability');
        return false;
      }
    },
    []
  );

  /**
   * Triggers Apple Pay payment sheet for the given amount
   * @param amountInCents - Amount to charge in cents
   * @returns ApplePayDetails if successful, null if cancelled/failed
   */
  const requestApplePayForReload = useCallback(
    async (amountInCents: number): Promise<ApplePayDetails | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const appleConfig = getConfiguration();
        if (!appleConfig) {
          throw new Error('Apple Pay configuration not found');
        }

        const isAvailable = await validateApplePayAvailability(appleConfig);
        if (!isAvailable) {
          return null;
        }

        const paymentConfig = buildPaymentConfig(amountInCents, appleConfig);

        if (!paymentConfig.merchantId) {
          throw new Error('Apple Pay merchant ID not configured');
        }

        logger.info({
          message: '[ApplePay Reload] Opening Apple Pay sheet',
          amount: amountInCents,
          processor: config.processor,
        });

        // Execute Apple Pay request with event listeners
        const applePayDetails = await new Promise<ApplePayDetails>((resolve, reject) => {
          const closedListener = addPaymentAuthorizationClosedListener(() => {
            logger.info({
              message: '[ApplePay Reload] Payment sheet closed by user',
            });
            closedListener.remove();
            authListener.remove();
            reject(new Error('User cancelled Apple Pay'));
          });

          const authListener = addPaymentAuthorizationListener(({ data }) => {
            logger.info({
              message: '[ApplePay Reload] Payment authorized',
            });

            closedListener.remove();
            authListener.remove();

            if (!data) {
              reject(new Error('No payment data received from Apple Pay'));
              return;
            }

            try {
              const details = parsePaymentResponse(data);
              resolve(details);
            } catch (err) {
              reject(err);
            }
          });

          const countryCode = region === 'CA' ? 'CA' : 'US';
          const itemLabel = 'Gift Card Reload';

          makePaymentRequest({
            countryCode,
            currencyCode: paymentConfig.currencyCode,
            total: parseFloat(paymentConfig.totalAmount),
            payeeName: paymentConfig.payeeName,
            merchantId: paymentConfig.merchantId,
            itemLabel,
          });
        });

        logger.info({
          message: '[ApplePay Reload] Payment successful',
          paymentType: applePayDetails.PaymentMethodData.paymentType,
        });

        return applePayDetails;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';

        // Don't log user cancellation as an error
        if (errorMessage === 'User cancelled Apple Pay') {
          logger.info({
            message: '[ApplePay Reload] User cancelled payment',
          });
        } else {
          logger.error({
            message: '[ApplePay Reload] Payment failed',
            error: errorMessage,
          });
          setError(errorMessage);
        }

        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [
      getConfiguration,
      validateApplePayAvailability,
      buildPaymentConfig,
      parsePaymentResponse,
      config.processor,
      region,
    ]
  );

  return {
    requestApplePayForReload,
    isLoading,
    error,
  };
}

